import { useSelector } from 'react-redux';
import {
    IconButton,
    Button,
    TextField,
    FormControlLabel,
    RadioGroup,
    Radio,
    Autocomplete,
    Checkbox,
    CircularProgress,
    FormHelperText,
    FormControl
} from '@mui/material';
import { HighlightOff, Description } from '@mui/icons-material';
import useAddTaxInvoiceDetails from '../hooks/useAddTaxInvoiceDetails';
import TaxInfoSidebar from './TaxInfoSidebar';
import { H5, H6 } from '../../../../../themes/typography';

function AddTaxInvoiceDetails({ 
    onClose = () => {}
}) {
    const { countries } = useSelector((state) => state.cities);

    const { 
        formik, 
        loading, 
        filterCitiesAndStates 
    } = useAddTaxInvoiceDetails({ onSuccess: onClose });

    return (
        <section id="add-tax-invoice-detail-section">
            <div className="tax-invoice-detail-wrapper">
                <div className="tax-invoice-header">
                    <div className="tax-invoice-detail-title">
                        <span className="invoice-info-icon">
                            <Description color="secondary" sx={{ fontSize: '1.5rem' }} />
                        </span>
                        <H5 fontWeight={600}>Tax Invoicing Details</H5>
                    </div>
                    <div className="tax-invoice-close-icon">
                        <IconButton onClick={onClose}>
                            <HighlightOff color="secondary" sx={{ fontSize: '1.75rem' }} />
                        </IconButton>
                    </div>
                </div>

                <div className="tax-invoice-details-content">
                    <div className="tax-invoice-details-left-content">
                        <form
                            id="tax-invoice-detail-form"
                            className="tax-invoice-detail-form-container"
                            onSubmit={formik.handleSubmit}
                            noValidate
                        >
                            <div className="tax-invoice-input-container">
                                <div className="tax-invoice-detail-two-input">
                                    <div className="tax-invoice-info-input">
                                        <TextField
                                            required
                                            fullWidth
                                            id="panNumber"
                                            label="PAN Number"
                                            name="panNumber"
                                            value={formik.values.panNumber}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'panNumber',
                                                    e.target.value.toUpperCase()
                                                )
                                            }
                                            error={
                                                formik.touched.panNumber &&
                                                Boolean(formik.errors.panNumber)
                                            }
                                            helperText={
                                                formik.touched.panNumber && formik.errors.panNumber
                                            }
                                            slotProps={{ htmlInput: { maxLength: 10 } }}
                                        />
                                    </div>
                                    <div className="tax-invoice-info-input"></div>
                                </div>
                                <div className="tax-invoice-detail-two-input">
                                    <div className="tax-invoice-info-input gst-radio-btns">
                                        <H6 fontWeight={400}>Are you registered for GST?</H6>
                                        <RadioGroup
                                            row
                                            aria-labelledby="gst-registration-label"
                                            name="gst-registration"
                                            sx={{ gap: '1.5rem', marginLeft: '0.5rem' }}
                                            value={formik.values.isGstRegistered}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'isGstRegistered',
                                                    e.target.value === 'true'
                                                )
                                            }
                                        >
                                            <FormControlLabel
                                                label="Yes"
                                                value={true}
                                                sx={{ gap: '0.75rem' }}
                                                control={<Radio sx={{ padding: '0' }} />}
                                            />
                                            <FormControlLabel
                                                label="No"
                                                value={false}
                                                sx={{ gap: '0.75rem' }}
                                                control={<Radio sx={{ padding: '0' }} />}
                                            />
                                        </RadioGroup>
                                    </div>
                                    <div className="tax-invoice-info-input">
                                        {formik.values.isGstRegistered && (
                                            <TextField
                                                fullWidth
                                                id="gstNumber"
                                                label="GST Number"
                                                name="gstNumber"
                                                value={formik.values.gstNumber}
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        'gstNumber',
                                                        e.target.value.toUpperCase()
                                                    )
                                                }
                                                error={
                                                    formik.touched.gstNumber &&
                                                    Boolean(formik.errors.gstNumber)
                                                }
                                                helperText={
                                                    formik.touched.gstNumber &&
                                                    formik.errors.gstNumber
                                                }
                                                slotProps={{ htmlInput: { maxLength: 15 } }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="tax-invoice-detail-two-input">
                                    <div className="tax-invoice-info-input">
                                        <TextField
                                            required
                                            fullWidth
                                            label="Address"
                                            name="address"
                                            id="address"
                                            value={formik.values.address}
                                            onChange={(e) =>
                                                formik.setFieldValue('address', e.target.value)
                                            }
                                            error={
                                                formik.touched.address &&
                                                Boolean(formik.errors.address)
                                            }
                                            helperText={
                                                formik.touched.address && formik.errors.address
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="tax-invoice-detail-two-input">
                                    <div className="tax-invoice-info-input">
                                        <Autocomplete
                                            fullWidth
                                            id="city"
                                            name="city"
                                            options={filterCitiesAndStates?.cities}
                                            getOptionLabel={(option) => option.name}
                                            onChange={(event, newValue) =>
                                                formik.setFieldValue('city', newValue)
                                            }
                                            inputValue={formik.values.city?.name}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="City"
                                                    error={
                                                        formik.touched.city &&
                                                        Boolean(formik.errors.city)
                                                    }
                                                    helperText={
                                                        formik.touched.city && formik.errors.city
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="tax-invoice-info-input">
                                        <Autocomplete
                                            fullWidth
                                            id="state"
                                            name="state"
                                            options={filterCitiesAndStates?.state}
                                            inputValue={formik.values.state?.name}
                                            getOptionLabel={(option) => option.name}
                                            onChange={(event, newValue) =>
                                                formik.setFieldValue('state', newValue)
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="State"
                                                    error={
                                                        formik.touched.state &&
                                                        Boolean(formik.errors.state)
                                                    }
                                                    helperText={
                                                        formik.touched.state && formik.errors.state
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="tax-invoice-detail-two-input">
                                    <div className="tax-invoice-info-input">
                                        <TextField
                                            required
                                            fullWidth
                                            label="Pin Code"
                                            name="pinCode"
                                            id="pinCode"
                                            value={formik.values.pinCode}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                formik.setFieldValue('pinCode', value);
                                            }}
                                            error={
                                                formik.touched.pinCode &&
                                                Boolean(formik.errors.pinCode)
                                            }
                                            helperText={
                                                formik.touched.pinCode && formik.errors.pinCode
                                            }
                                            slotProps={{ htmlInput: { maxLength: 6 } }}
                                        />
                                    </div>
                                    <div className="tax-invoice-info-input">
                                        <Autocomplete
                                            fullWidth
                                            id="country"
                                            name="country"
                                            options={countries}
                                            disabled={true}
                                            defaultValue={countries[0]}
                                            value={formik.values.country}
                                            getOptionLabel={(option) => option.name}
                                            onChange={(event, newValue) =>
                                                formik.setFieldValue('country', newValue)
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Country"
                                                    error={
                                                        formik.touched.country &&
                                                        Boolean(formik.errors.country)
                                                    }
                                                    helperText={
                                                        formik.touched.country &&
                                                        formik.errors.country
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="kyc-tnc">
                                <H6 fontWeight={400}>
                                    🛡️ This information is encrypted and is completely secure with
                                    us
                                </H6>
                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                sx={radioBtnStyle}
                                                color="secondary"
                                                size="small"
                                                name="isTermsAccepted"
                                                checked={formik.values.isTermsAccepted}
                                                onChange={(e) =>
                                                    formik.setFieldValue(
                                                        'isTermsAccepted',
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label="By clicking here, I acknowledge that i have read, understood & accept the Terms of Service & the Privacy Policy."
                                        sx={{
                                            fontSize: '0.875rem',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                    <FormHelperText
                                        error={
                                            formik.touched.isTermsAccepted &&
                                            Boolean(formik.errors.isTermsAccepted)
                                        }
                                    >
                                        {formik.touched.isTermsAccepted &&
                                            formik.errors.isTermsAccepted}
                                    </FormHelperText>
                                </FormControl>
                            </div>
                        </form>
                        <div className="tax-invoice-detail-form-action-button">
                            <Button
                                variant="main"
                                endIcon={
                                    loading ? <CircularProgress size={20} color="inherit" /> : null
                                }
                                onClick={formik.handleSubmit}
                                disabled={loading}
                            >
                                Submit
                            </Button>
                        </div>
                    </div>
                    <div className="tax-invoice-details-right-content">
                        <TaxInfoSidebar />
                    </div>
                </div>
            </div>
        </section>
    );
}

export default AddTaxInvoiceDetails;

const radioBtnStyle = {
    padding: '0'
};
